import { useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import { useAuthStore } from '../../../stores/auth';
import { usePermissions } from '../../../hooks/usePermissions';
import {
  useSite,
  useSiteAssignments,
  useOrganizationMembers,
  useSiteInvitations,
  useSiteStats,
  useFindingsTrend,
} from '../../../hooks/useSites';
import { getInspections } from '../../../lib/inspections';
import { SITE_LIFECYCLE_LABELS, getSiteLifecycleBadgeTone } from '../../../lib/organizations';
import { getReports } from '../../../lib/reports';
import { isSiteInfoLocked, siteInfoLockedReason } from '../../../lib/siteInfoManage';
import { StickyHeader, StickyHeaderAction } from '../../../components/common';
import { useDeviceLayout } from '../../../hooks/useDeviceLayout';
import { useSiteDetailState } from './useSiteDetailState';
import { SiteDetailMobile } from './SiteDetailMobile';
import { SiteDetailDesktop } from './SiteDetailDesktop';
import {
  AssignModal,
  UnassignModal,
  InvitationCancelModal,
  AssigneeDetailModalWrapper,
} from './components/Modals';
import { ExclamationIcon, XIcon } from './components/Icons';
import { SiteDetailData, SiteDetailLayoutProps, parseSiteDetailTab } from './types';
import { Badge } from '@safetyhub/ui';

/**
 * 현장 상세 페이지
 *
 * ResponsivePage 패턴을 적용하여 모바일/데스크톱 레이아웃을 분리합니다.
 */
export default function SiteDetail() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { siteId } = useParams<{ siteId: string }>();
  // `?tab=approval` — 「결재 관리」(/settings/approval)의 현장 칩이 결재선 탭으로 곧장 들어오는 입구.
  // 초기 탭만 정하고 이후 탭 전환은 URL과 동기화하지 않는다(종전 동작 유지).
  const [searchParams] = useSearchParams();
  const initialDetailTab = parseSiteDetailTab(searchParams.get('tab'));
  const { currentMembership } = useAuthStore();
  const companyId = currentMembership?.companyId;
  const memberId = currentMembership?.id;
  // 조직 RBAC 키(P4 ②) — 옛 `role === 'OWNER'` 대신 자리마다 키 하나. 하위 섹션 prop 이름 `isOwner`는 호환용이다.
  const { can, canManageSites: canManageSite, isOwner } = usePermissions();
  // 담당자 배정·해제는 서버가 owner 전용을 유지한다(현행 넓이 보존 — sites.service assignMember/unassignMember isOwnerMember).
  // `site.assign` 키로 넓히는 것은 정책 결정(P7 전 사용자 판단)이라 화면도 소유자 성질을 읽는다.
  const canAssign = isOwner;
  /**
   * 담당자 **유형 변경**(정/부)은 위 배정·해제와 **다른 축**이다 — 서버 `sites.service.updateAssignment`는
   * `isOwnerMember`를 요구하지 않고 컨트롤러 가드 `site.assign`만 지난다(2026-09-24 승인). 화면이 오래도록
   * 두 축을 `isOwner` 하나로 합쳐 읽어, 시드 안전관리자는 서버가 허용하는 행위에 **입구가 없었다**.
   *
   * 벽(공석 충원 ↔ 교체·강등)은 이 키와 **AND**로 걸린다 — `lib/sitePrimaryDesignation.ts`, 결정 기록
   * `docs/decisions/2026-09-26-primary-assignee-designation-wall.md`.
   */
  const canChangeAssignmentType = can('site.assign');
  const canInvite = can('member.invite');
  const canManageApprovalLines = can('approval.line.manage');

  // 디바이스 레이아웃 감지
  const deviceLayout = useDeviceLayout();
  const isMobile = deviceLayout === 'phone' || deviceLayout === 'fold';

  // React Query로 데이터 조회
  const { data: site, isLoading: siteLoading } = useSite(companyId, siteId);
  const { data: assignments = [], isLoading: assignmentsLoading } = useSiteAssignments(
    companyId,
    siteId
  );
  const { data: members = [] } = useOrganizationMembers(companyId);
  const { data: siteInvitations = [] } = useSiteInvitations(companyId, siteId, canInvite);
  const { data: siteStats } = useSiteStats(companyId, siteId);

  // 상태 관리 훅
  const { state, handlers, permissions } = useSiteDetailState({
    companyId,
    siteId,
    memberId,
    canManageSite,
    canAssign,
    canInvite,
    canManageApprovalLines,
    assignments,
    members,
    queryClient,
    initialDetailTab,
  });

  // 위험요인 추이 데이터
  const { data: findingsTrend } = useFindingsTrend(companyId, siteId, state.trendPeriod);

  // 최근 점검/보고서 데이터 조회
  const { data: recentInspections } = useQuery({
    queryKey: ['siteInspections', siteId],
    queryFn: async () => {
      const response = await getInspections(companyId!, { siteId, limit: 5 });
      return response.data || [];
    },
    enabled: !!companyId && !!siteId,
  });

  const { data: recentReports } = useQuery({
    queryKey: ['siteReports', siteId],
    queryFn: async () => {
      const response = await getReports(companyId!, { siteId, limit: 5 });
      return response.data || [];
    },
    enabled: !!companyId && !!siteId,
  });

  const isLoading = siteLoading || assignmentsLoading;

  const isTransferPending = site?.status === 'TRANSFER_PENDING';

  // StickyHeader 액션 버튼 (이관대기 중에는 수정 버튼 비활성화)
  const stickyHeaderActions = useMemo((): StickyHeaderAction[] => {
    if (!permissions.canEdit || isTransferPending) return [];

    // O10 — 정보 관리권이 업체에 있으면 비활성 + 사유(업체가 활성 멤버를 모두 잃으면 풀리는 막힘이라 감추지 않는다)
    const infoLocked = isSiteInfoLocked(site);
    return [
      {
        key: 'edit',
        label: '수정',
        onClick: () => navigate(`/sites/${siteId}/edit`),
        variant: 'secondary',
        disabled: infoLocked,
        tooltip: infoLocked ? siteInfoLockedReason() : undefined,
      },
    ];
  }, [permissions.canEdit, isTransferPending, site, siteId, navigate]);

  // 로딩 상태
  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
      </div>
    );
  }

  // 데이터 없음
  if (!site) {
    return (
      <div className="text-center py-12">
        <p className="text-ink-500">현장을 찾을 수 없습니다.</p>
        <button
          onClick={() => navigate('/sites')}
          className="mt-4 text-brand-600 hover:text-brand-800"
        >
          현장 목록으로 돌아가기
        </button>
      </div>
    );
  }

  // 레이아웃에 전달할 데이터
  const data: SiteDetailData = {
    site,
    assignments,
    members,
    siteInvitations,
    siteStats,
    findingsTrend,
    recentInspections,
    recentReports,
  };

  // 레이아웃 props
  const layoutProps: SiteDetailLayoutProps = {
    data,
    layout: deviceLayout,
    isMobile,
    isPhone: deviceLayout === 'phone',
    isFold: deviceLayout === 'fold',
    companyId: companyId!,
    siteId: siteId!,
    memberId,
    state,
    handlers,
    permissions,
    queryClient,
    navigate,
  };

  // 레이아웃 컴포넌트 선택
  const Layout = isMobile ? SiteDetailMobile : SiteDetailDesktop;

  return (
    <div className="space-y-6">
      {/* Sticky 헤더 */}
      <StickyHeader backLink="/sites" backLabel="현장 목록" actions={stickyHeaderActions} />

      {/* 제목 및 정보 */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center gap-2 flex-wrap">
          <h1 className="text-2xl font-bold text-ink-900">{site.name}</h1>
          {/* 생애주기 배지 — '운영중'은 기본값이라 굳이 달지 않는다(신호 없으면 미표시).
              이관대기·비활성 배지를 이 한 배지로 흡수했다. */}
          {site.status !== 'ACTIVE' && (
            <Badge tone={getSiteLifecycleBadgeTone(site.status)} dot>
              {SITE_LIFECYCLE_LABELS[site.status]}
            </Badge>
          )}
        </div>
        {site.address && <p className="mt-1 text-sm text-ink-500">{site.address}</p>}
      </div>

      {/* 콘텐츠 영역 */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">
        {/* 이관대기 알림 배너 */}
        {isTransferPending && (
          <div className="bg-orange-50 border border-orange-200 text-orange-700 px-4 py-3 rounded-card flex items-start gap-2">
            <ExclamationIcon className="h-5 w-5 flex-shrink-0 mt-0.5" />
            <p className="text-sm">이관 대기 중인 현장입니다. 이관이 완료되거나 취소될 때까지 수정할 수 없습니다.</p>
          </div>
        )}
        {/* 에러 메시지 */}
        {state.error && (
          <div className="rounded-card bg-red-50 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <ExclamationIcon className="h-5 w-5 text-red-400" />
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-700">{state.error}</p>
              </div>
              <div className="ml-auto pl-3">
                <button
                  onClick={() => state.setError('')}
                  className="inline-flex text-red-400 hover:text-red-500"
                >
                  <XIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 레이아웃 */}
        <Layout {...layoutProps} />
      </div>

      {/* 모달들 */}
      <AssignModal
        state={state}
        handlers={handlers}
        permissions={permissions}
        assignments={assignments}
      />

      <UnassignModal
        unassignTarget={state.unassignTarget}
        setUnassignTarget={state.setUnassignTarget}
        isUnassigning={state.isUnassigning}
        handleUnassign={handlers.handleUnassign}
      />

      <InvitationCancelModal
        invitationCancelTarget={state.invitationCancelTarget}
        setInvitationCancelTarget={state.setInvitationCancelTarget}
        isCancellingInvitation={state.isCancellingInvitation}
        handleCancelInvitation={handlers.handleCancelInvitation}
      />

      <AssigneeDetailModalWrapper
        selectedAssignment={state.selectedAssignment}
        setSelectedAssignment={state.setSelectedAssignment}
        setUnassignTarget={state.setUnassignTarget}
        members={members}
        canChangeAssignmentType={canChangeAssignmentType}
        canUnassign={canAssign}
        viewerMemberId={memberId}
        isOwner={isOwner}
        canManageSite={canManageSite}
        assignments={assignments}
        handleUpdateAssignmentType={handlers.handleUpdateAssignmentType}
      />
    </div>
  );
}
